/** @jsx jsx */

import { useState } from 'react';
import { css, jsx } from '@emotion/react';

import { Badge } from '../../display/Badge';
import { FloatingBox } from '../../display/FloatingBox';
import { Tag } from '../../display/Tag';
import { IconButton } from '../../navigation/IconButton';
import { Menu, MenuItem } from '../../forms/Menu';
import { useClickOutside } from '../../../hooks';
import { labelStyles } from '@zapier/style-encapsulation';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';

type Props = {
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<TabsItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: TabsSize;
};

const Styles = labelStyles('TabsDropdown', {
  root: css`
    position: relative;
  `,

  summary: css`
    // Hides default icon on details element
    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      display: none;
    }
  `,

  menu: css`
    padding: var(--zds-space-12);
    min-width: 250px;
  `,

  badgeAndTag: css`
    margin-left: var(--zds-space-8);
  `,
});

export const TabsDropdown = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = props.items.map((item) => (
    <MenuItem
      disabled={item.disabled}
      href={item.href}
      isSelected={item.href === props.selectedItemHref}
      key={item.href}
      onClick={(event: React.MouseEvent) => {
        setIsOpen(false);
        props.onChooseItem(item, event);
      }}
    >
      {item.text}
      {item.badgeText && (
        <Badge variant={item.badgeVariant} css={Styles.badgeAndTag}>
          {item.badgeText}
        </Badge>
      )}
      {item.tagText && (
        <Tag variant={item.tagVariant} css={Styles.badgeAndTag}>
          {item.tagText}
        </Tag>
      )}
    </MenuItem>
  ));
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const containerHandlers = {
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
  };
  return (
    <div ref={setClickOutsideNode} {...containerHandlers} data-zds>
      <IconButton
        color="icon-tertiary"
        icon="navMoreHoriz"
        label="More items…"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        size={'small'}
        tooltipPosition="west"
      />
      {isOpen && (
        <FloatingBox align="right">
          <div css={Styles.menu} data-zds>
            <Menu aria-label="">{menuItems}</Menu>
          </div>
        </FloatingBox>
      )}
    </div>
  );
};
